import React, { useState } from 'react';
import { Avatar, AvatarSize, BrandedAvatar, Button } from '@design-system';
import { BellIcon, QuestionIcon } from '@chakra-ui/icons';
import meta from '../../../../meta.json';
import { Tooltip, Badge, Box } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translations } from 'translations';
import {
  getUserAvatarUrl,
  getDefaultUserAvatarUrl,
  generateGuestUserAvatar,
} from 'pages/BrandingPage/services/brandingService';
import { useInvitations } from 'hooks/useInvitations';
import jwtDecode from 'jwt-decode';
import { useLocation } from 'react-router-dom';
import { SecuritySettingsModal } from './SecuritySettingsModal';
import { LogoutConfirmationModal } from './LogoutConfirmationModal';
import { useSession } from 'store/session';

const SafeLink = Link as any;

interface SidebarUserFooterProps {
  user: any;
  isLoggedIn: boolean;
  isCollapsed: boolean;
  handleLogout: () => void;
  primaryColor?: string;
  onGuestLogin?: () => void;
}

export const SidebarUserFooter: React.FC<SidebarUserFooterProps> = ({
  user,
  isLoggedIn,
  isCollapsed,
  handleLogout,
  primaryColor = '#6366f1',
  onGuestLogin,
}) => {
  const { t } = useTranslation();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const { idleTimeout } = useSession();

  const {
    invitations,
    invitationsCount,
    isLoading,
    handleAcceptInvitation,
    handleRejectInvitation,
  } = useInvitations();

  const location = useLocation();

  // Logic identifying guest user from token
  const guestUser = React.useMemo(() => {
    if (isLoggedIn) return null;

    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get('token');

    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      if (decoded && decoded.email) {
        const email = decoded.email;
        const name = email.split('@')[0];
        const avatar = generateGuestUserAvatar(email, primaryColor);

        return { name, email, avatarUrl: avatar };
      }
    } catch (e) {
      console.error('Error decoding guest token', e);
    }
    return null;
  }, [isLoggedIn, location.search]);

  if (!isLoggedIn && !guestUser) return null;

  // If guest has no OTP login handler, they're an external guest — render minimal footer
  const isExternalVepormasGuestFooter = !!guestUser && !onGuestLogin;

  const currentName = guestUser ? guestUser.name : (user?.name || '');
  const userName = currentName.split(' ')[0] || currentName;
  const userEmail = guestUser ? guestUser.email : user?.email;

  const avatarUrl = guestUser
    ? guestUser.avatarUrl
    : (user?.id ? getUserAvatarUrl(user.id) : getDefaultUserAvatarUrl());

  const profileLinkTo = guestUser
    ? '#'
    : { pathname: '/profile', search: '?tab=user' };

  const handleOpenQuotePage = () => {
    window.open('https://www.cincel.digital/cotizar/', '_blank');
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen((prev) => !prev);
  };

  if (isCollapsed) {
    // External guest in collapsed mode: just show avatar, no actions
    if (isExternalVepormasGuestFooter) {
      return (
        <div className="mt-auto py-4">
          <div className="mb-4 h-px -mx-4 bg-gray-400" />
          <div className="flex flex-col items-center gap-3">
            <Tooltip
              label={
                <div className="py-1">
                  <div className="font-medium">{userName}</div>
                  <div className="text-xs text-gray-300">{userEmail}</div>
                </div>
              }
              placement="right"
            >
              <div className="block p-1">
                <BrandedAvatar
                  alt="User profile"
                  src={avatarUrl}
                  size={AvatarSize.sm}
                  primaryColor={primaryColor}
                />
              </div>
            </Tooltip>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-auto py-4">
        {/* Divider */}
        <div className="mb-4 h-px -mx-4 bg-gray-400" />
        <div className="flex flex-col items-center gap-3">
          {/* Notifications - Collapsed (solo si hay invitaciones) */}
          {invitationsCount > 0 && (
            <Tooltip
              label={`${invitationsCount} invitación${invitationsCount > 1 ? 'es' : ''} pendiente${invitationsCount > 1 ? 's' : ''}`}
              placement="right"
            >
              <Box position="relative" cursor="pointer">
                <BellIcon color="white" w={5} h={5} />
                <Badge
                  colorScheme="red"
                  borderRadius="full"
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  fontSize="10px"
                  minW="16px"
                  h="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {invitationsCount}
                </Badge>
              </Box>
            </Tooltip>
          )}

          {/* Utilities Row - Collapsed */}
          <div className="flex flex-col gap-4 mt-2">
            <Tooltip label={t(translations.SIDEBAR.SUPPORT)} placement="right">
              <a
                href="https://ayuda.cincel.digital/kb-tickets/new"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m-.01 4.01h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </a>
            </Tooltip>

            <Tooltip label={t(translations.COMMON.QUOTE) as string} placement="right">
              <button
                onClick={handleOpenQuotePage}
                className="flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </Tooltip>
            <Tooltip label={t(translations.SECURITY.TITLE, { defaultValue: 'Seguridad' })} placement="right">
              <button
                onClick={() => setIsSecurityOpen(true)}
                className="flex items-center justify-center text-gray-400 hover:text-blue-400 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </button>
            </Tooltip>
          </div>

          {/* User Avatar - Collapsed */}
          <Tooltip
            label={
              <div className="py-1">
                <div className="font-medium">{userName}</div>
                <div className="text-xs text-gray-300">{t(translations.COMMON.PROFILE)}</div>
              </div>
            }
            placement="right"
          >
            <SafeLink to={profileLinkTo} className="block p-1">
              <BrandedAvatar
                alt="User profile"
                src={avatarUrl}
                size={AvatarSize.sm}
                primaryColor={primaryColor}
                showEditBadge
              />
            </SafeLink>
          </Tooltip>

          {/* Logout - Collapsed */}
          <Tooltip label={t(translations.COMMON.LOGOUT) as string} placement="right">
            <button
              onClick={() => setIsLogoutModalOpen(true)}
              className="text-gray-400 hover:text-red-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </Tooltip>
        </div>
      </div>
    );
  }

  // External guest expanded: minimal footer with just avatar/name/email
  if (isExternalVepormasGuestFooter) {
    return (
      <div className="mt-auto px-4 py-4">
        <div className="mb-4 h-px -mx-4 bg-gray-400" />
        <div className="flex items-center py-2 overflow-hidden">
          <div className="flex min-w-0 flex-1 items-center gap-3 p-1">
            <BrandedAvatar
              alt="User profile"
              src={avatarUrl}
              size={AvatarSize.sm}
              primaryColor={primaryColor}
              className="shrink-0"
            />
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-white text-sm font-bold truncate" title={currentName}>{currentName}</span>
              <span className="text-gray-300 text-xs truncate" title={userEmail}>{userEmail}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-auto px-4 py-4">
      {/* Divider */}
      <div className="mb-4 h-px -mx-4 bg-gray-400" />
      {/* Notifications Row - Solo si hay invitaciones */}
      {invitationsCount > 0 && (
        <div className="mb-3">
          <button
            onClick={toggleNotifications}
            className="flex items-center gap-3 w-full text-gray-300 hover:text-white transition-colors py-2"
          >
            <Box position="relative">
              <BellIcon w={5} h={5} />
              <Badge
                colorScheme="red"
                borderRadius="full"
                position="absolute"
                top="-8px"
                right="-10px"
                fontSize="10px"
                minW="16px"
                h="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {invitationsCount}
              </Badge>
            </Box>
            <span className="text-sm">Invitaciones</span>
            <span className="ml-auto text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
              {invitationsCount}
            </span>
          </button>

          {/* Invitations Panel */}
          {isNotificationsOpen && (
            <div className="mt-2 ml-8 space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="bg-gray-800 rounded-lg p-3 border border-gray-700"
                >
                  <div className="text-sm text-white mb-1">
                    <strong>{invitation.sender?.email}</strong>
                  </div>
                  <div className="text-xs text-gray-400 mb-3">
                    {t(translations.USER_DROPDOWN.INVITATION_MESSAGE)}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="xs"
                      color={primaryColor}
                      variant="solid"
                      isLoading={isLoading}
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      className="flex-1 text-white"
                    >
                      {t(translations.COMMON.ACCEPT)}
                    </Button>
                    <Button
                      size="xs"
                      color="#6b7280"
                      variant="solid"
                      isLoading={isLoading}
                      onClick={() => handleRejectInvitation(invitation.id)}
                      className="flex-1 text-white"
                    >
                      {t(translations.COMMON.REJECT)}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Utilities Row (Support | Quote | Version) */}
      <div className="flex items-center justify-between mb-4 px-1">
        <Tooltip label={t(translations.SIDEBAR.SUPPORT)}>
          <a
            href="https://ayuda.cincel.digital/kb-tickets/new"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m-.01 4.01h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Ayuda</span>
          </a>
        </Tooltip>

        <Tooltip label={t(translations.COMMON.QUOTE)}>
          <button
            onClick={handleOpenQuotePage}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Cotizar</span>
          </button>
        </Tooltip>

        <Tooltip label={t(translations.SECURITY.TITLE, { defaultValue: 'Seguridad' })}>
          <button
            onClick={() => setIsSecurityOpen(true)}
            className="flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-xs">{t(translations.SECURITY.TITLE, { defaultValue: 'Bloqueo' })}</span>
          </button>
        </Tooltip>
      </div>

      {/* User Profile Section + Logout */}
      <div className="flex items-center justify-between py-2 overflow-hidden">
        <SafeLink
          to={profileLinkTo}
          onClick={(e: any) => {
            if (guestUser && onGuestLogin) {
              e.preventDefault();
              onGuestLogin();
            }
          }}
          className="flex min-w-0 flex-1 items-center gap-3 hover:opacity-80 transition-opacity p-1"
        >
          <BrandedAvatar
            alt="User profile"
            src={avatarUrl}
            size={AvatarSize.sm}
            primaryColor={primaryColor}
            className="shrink-0"
            showEditBadge
          />
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-white text-sm font-bold truncate" title={currentName}>{currentName}</span>
            <span className="text-gray-300 text-xs truncate" title={userEmail}>{userEmail}</span>
          </div>
        </SafeLink>

        <Tooltip label={guestUser ? t(translations.COMMON.LOGIN) : t(translations.COMMON.LOGOUT)} placement="top">
          <button
            onClick={() => {
              if (guestUser && onGuestLogin) {
                onGuestLogin();
              } else {
                setIsLogoutModalOpen(true);
              }
            }}
            className={`p-2 ${guestUser ? 'text-gray-400 hover:text-green-400' : 'text-gray-400 hover:text-red-400'} hover:bg-gray-800 rounded-lg transition-colors`}
            aria-label={guestUser ? t(translations.COMMON.LOGIN) : t(translations.COMMON.LOGOUT)}
          >
            {guestUser ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            )}
          </button>
        </Tooltip>
      </div>

      <SecuritySettingsModal
        isOpen={isSecurityOpen}
        onClose={() => setIsSecurityOpen(false)}
      />

      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onLogout={handleLogout}
        primaryColor={primaryColor}
      />
    </div >
  );
};
